import type { FieldPath, FieldPathValue, FieldValues, RegisterOptions } from "react-hook-form";
import { useController } from "react-hook-form";

import type { CheckboxProps } from "@/components/Checkbox/Checkbox";
import { Checkbox } from "@/components/Checkbox/Checkbox";

interface FormCheckboxProps<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
> extends Omit<CheckboxProps, "checked"> {
  "data-testid"?: string;
  name: TName;
  defaultValue?: FieldPathValue<TFormValues, TName>;
  label: string;
  rules?: RegisterOptions<TFormValues, TName>;
  memorise?: boolean;
}

export function FormCheckbox<
  TFormValues extends FieldValues,
  TName extends FieldPath<TFormValues> = FieldPath<TFormValues>,
>({
  "data-testid": dataTestId,
  name,
  rules,
  defaultValue,
  memorise,
  color,
  shape,
  alignTop,
  ...props
}: FormCheckboxProps<TFormValues, TName>): React.ReactNode {
  const { field } = useController<TFormValues, TName>({ name, rules, defaultValue });
  let colorStyle = color;
  if (memorise) {
    colorStyle = field.value ? "blue" : "green";
  }

  return (
    <Checkbox
      data-testid={dataTestId}
      {...field}
      {...props}
      onChange={(e) => {
        field.onChange(e);
        props.onChange?.(e);
      }}
      name={name}
      checked={!!field.value}
      shape="square"
      color={colorStyle}
      label={props.label}
      alignTop={alignTop}
      tooltip={props.tooltip}
    />
  );
}
